<template>
  <Overlay data-for="export-button" placement="below" @close="$emit('close')">
    <div class="download-container">
      <div class="download-options">
        <div class="download-option" :key="index" v-for="(option,index) in filteredOptions"
             @click="handleExportOptionOnClick(option.type)">
          {{ option.title }}
        </div>
      </div>
    </div>
  </Overlay>
</template>
<script lang="ts">
import { computed, defineComponent } from 'vue'
import { Overlay } from '@shared-components'
import { useI18n } from 'vue-i18n'
import { FileTypeEnum, arrayIsEmpty, convertToExcel, downloadFile, stringIsEmpty } from '@ipevo-rd/service-web-utils'
import { useInfoStore } from '@shared-store'
import { findLanguageItemsBySupportAzureSTT } from '@shared-data'

export default defineComponent({
  name: 'ExportOverlay',
  components: { Overlay },
  emits: ['close'],
  props: {
    // 逐字稿
    data: {
      type: Array,
      required: true,
    },
    // 摘要
    summary: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    /**
     * 下載類型Enum
     * @enum {DownloadType}
     */
    enum DownloadType {
      /**
       * 逐字稿
       */
      Transcript = 0,
      /**
       * 精華摘要
       */
      Summary = 1,
    }

    interface DownloadOption {
      /**
       * 語言
       */
      type: DownloadType
      /**
       * 標題
       */
      title: string,
      /**
       * 是否顯示
       */
      show: boolean,
    }


    // 取得i18n實例
    const { t } = useI18n()
    // 取得當前會議支持的翻譯語言 / 當前會議的短ID
    const { supportedTranslationLanguages, shortID } = useInfoStore()


    // 選單選項
    const options = computed<Array<DownloadOption>>(() => [
      //逐字稿
      {
        type: DownloadType.Transcript,
        title: t('tutorialPage.downloadOverlay.transcript'),
        show: !arrayIsEmpty(props.data),
      },
      //精華摘要
      {
        type: DownloadType.Summary,
        title: t('tutorialPage.downloadOverlay.summary'),
        show: !stringIsEmpty(props.summary),
      },
    ])
    // 過濾掉不顯示的選項
    const filteredOptions = computed(() => options.value.filter(option => option.show))


    /**
     * 將逐字稿輸出成 Excel 檔案
     * @return {void}
     */
    const exportTranscriptToExcel = (): void => {
      // 語系代碼
      const allLang = supportedTranslationLanguages
      // 語系顯示名稱
      const allLangHeaders = findLanguageItemsBySupportAzureSTT(allLang)

      // 對應欄位 key
      const fields = ['original_text', ...allLangHeaders.map(lang => lang.locale)]
      // 對應標題
      const headers = [t('tutorialPage.downloadOverlay.originText'), ...allLangHeaders.map(lang => lang.title)]

      // 每筆 row 的 key 必須對應 fields
      const rows = props.data.map((item: any) => {
        // 新增 Row用於存放原始文字和翻譯文字
        const row: Record<string, string> = { original_text: item.original_text }
        // 將每個語言的翻譯文字加入 row
        allLang.forEach(lang => {
          row[lang] = item.translated_text?.[lang] ?? ''
        })

        // 返回處理後的 row
        return row
      })

      // 表單資料
      const sheetsData = {
        Sheet1: {
          headers,
          fields,
          data: rows,
        },
      }

      // 轉換為 Excel Blob
      const blob = convertToExcel(sheetsData)
      // 下載檔案
      downloadFile(`${shortID}`, FileTypeEnum.XLSX, blob)
    }

    /**
     * 將摘要輸出成 TXT 檔案
     * @return {void}
     */
    const exportSummaryToTxt = (): void => {
      // 產生 Blob 物件
      const blob = new Blob([props.summary], { type: 'text/plain' })
      // 下載檔案
      downloadFile(`${shortID}-Summary`, FileTypeEnum.TXT, blob)
    }

    /**
     * 處理下載選項點擊事件
     * @param type {DownloadType} 下載類型
     * @return {void}
     */
    const handleExportOptionOnClick = (type: DownloadType): void => {
      // 定義下載類型對應的操作
      const actions = {
        // 逐字稿
        [DownloadType.Transcript]: exportTranscriptToExcel,
        // 摘要
        [DownloadType.Summary]: exportSummaryToTxt,
      }
      // 執行對應的操作
      actions[type]?.()

      // 關閉彈窗
      emit('close')
    }


    return {
      filteredOptions,
      handleExportOptionOnClick,
    }
  },
})
</script>

<style scoped lang="scss">
// 啟用時的顏色
$active-color: rgba(221, 221, 221, 1);
// 標題字體大小
$header-font-weight: 600;
// 選項字體粗細
$option-font-weight: 400;
// 選項字體大小
$option-font-size: 16px;
// 選項行高
$option-line-height: 24px;
// 選項字母間距
$option-letter-spacing: 0.15px;
// 選項文字顏色
$option-text-color: rgba(0, 0, 0, 0.87);
// 選項內邊距
$option-padding: 12px 0;

.download-container {
  .download-options {
    .download-option {
      padding: $option-padding;
      text-align: center;
      font-size: $option-font-size;
      font-weight: $option-font-weight;
      line-height: $option-line-height;
      letter-spacing: $option-letter-spacing;
      cursor: pointer;
      transition: all 0.2s ease;
      color: $option-text-color;
      border-bottom: 1px solid #e0e0e0;
      margin: 0;
      position: relative;

      &:last-child {
        border-bottom: none;
      }

      &:hover:not(.active) {
        background-color: rgba($active-color, 0.5);
      }

      &.active {
        background-color: $active-color;
      }
    }
  }
}
</style>
